import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Toggle from '../toggle/toggle';

const GLAccountSegmentConfiguration = ({ 
    updatedClient, 
    data, 
    updateField 
}) => {
    const defaultSegments = [
        { key: 'GLUnit', labelKey: 'invoice.unit' },
        { key: 'GLOffice', labelKey: 'office' },
        { key: 'GLDepartement', labelKey: 'invoice.gl_departement' },
        { key: 'GLSection', labelKey: 'invoice.gl_section' },
        { key: 'GLPracticeGroup', labelKey: 'invoice.gl_practice_group' },
        { key: 'GLNatural', labelKey: 'gl_natural' },
        { key: 'GLTimekeeper', labelKey: 'timekeeper' }
    ];

    const [orderedSegments, setOrderedSegments] = useState(defaultSegments);

    // Initialize order from config or use default
    useEffect(() => {
        const savedOrder = updatedClient.config?.gl_account_segment_order;
        if (savedOrder && Array.isArray(savedOrder)) {
            // Reorder segments based on saved order
            const ordered = savedOrder
                .map(key => defaultSegments.find(s => s.key === key))
                .filter(Boolean);
            // Add any missing segments at the end
            const missing = defaultSegments.filter(s => !savedOrder.includes(s.key));
            setOrderedSegments([...ordered, ...missing]);
        } else {
            setOrderedSegments(defaultSegments);
        }
    }, [updatedClient.config?.gl_account_segment_order]);

    const updateSegmentField = (segmentKey, field, value) => {
        const currentConfig = updatedClient.config?.gl_account_segment_configuration || {};
        const currentSegment = currentConfig[segmentKey] || {};
        const updatedSegment = { ...currentSegment, [field]: value };
        const updatedConfig = { ...currentConfig, [segmentKey]: updatedSegment };
        updateField("config", updatedConfig, "gl_account_segment_configuration");
    };

    const handleDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const items = Array.from(orderedSegments);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setOrderedSegments(items);

        // Save the new order to config
        const newOrder = items.map(item => item.key);
        updateField("config", newOrder, "gl_account_segment_order");
    };

    return (
        <div className='row mt-3'>
            <div className='col-2'>{data.t.read("client_table.gl_account_segment_configuration")} :</div>
            <div className='col-10'>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="segments" direction="horizontal">
                        {(provided) => (
                            <div 
                                className='d-flex gap-4'
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {orderedSegments.map((segment, index) => {
                                    const segmentConfig = updatedClient.config?.gl_account_segment_configuration?.[segment.key] || {};
                                    const isActive = segmentConfig.active ?? false;
                                    
                                    return (
                                        <Draggable 
                                            key={segment.key} 
                                            draggableId={segment.key} 
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        opacity: snapshot.isDragging ? 0.8 : 1,
                                                        cursor: 'grab'
                                                    }}
                                                    className='mr-2'
                                                >
                                                    <Toggle
                                                        isOn={isActive}
                                                        handleToggle={(e) => {
                                                            const active = e.target.checked;
                                                            updateSegmentField(segment.key, "active", active);
                                                        }}
                                                        label={data.t.read(segment.labelKey)}
                                                        id={`gl-segment-${segment.key.toLowerCase()}-toggle`}
                                                    />
                                                    <input 
                                                        className='form-control mt-1'
                                                        value={segmentConfig.default_value || ""}
                                                        onChange={(e) => updateSegmentField(segment.key, "default_value", e.target.value)}
                                                        placeholder={`${data.t.read(segment.labelKey)}`}
                                                        disabled={!isActive}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
};

export default GLAccountSegmentConfiguration;
